import React, { useState, useMemo } from 'react';
import {
  Plus, X, Trash2, CheckCircle,
  ChevronDown,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUserData } from '../context/UserDataContext';
import EmptyState from './EmptyState';

const fmt    = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);

const DEBT_TYPES = [
  { id: 'credit_card',   label: 'Credit Card',   emoji: '💳' },
  { id: 'student_loan',  label: 'Student Loan',   emoji: '🎓' },
  { id: 'car_loan',      label: 'Car Loan',       emoji: '🚗' },
  { id: 'mortgage',      label: 'Mortgage',       emoji: '🏠' },
  { id: 'personal',      label: 'Personal Loan',  emoji: '💵' },
  { id: 'medical',       label: 'Medical',        emoji: '🏥' },
  { id: 'other',         label: 'Other',          emoji: '📋' },
];

const EMPTY_FORM = {
  name: '', type: 'credit_card', balance: '', interestRate: '', minimumPayment: '',
};

// ── Payoff calculator ─────────────────────────────────────────────────────────
function calcPayoff(debts, extraPayment, strategy) {
  if (!debts || debts.length === 0) return { months: 0, totalInterest: 0, schedule: [] };

  // Sort by strategy
  const sorted = [...debts].sort((a, b) => {
    if (strategy === 'snowball') return (a.balance || 0) - (b.balance || 0);
    return (b.interestRate || 0) - (a.interestRate || 0); // avalanche
  });

  let remaining = sorted.map(d => ({
    ...d,
    balance: d.balance || 0,
    paidOff: false,
    monthPaidOff: null,
  }));

  let month = 0;
  let totalInterest = 0;
  const MAX_MONTHS = 600; // 50 years cap

  while (remaining.some(d => !d.paidOff) && month < MAX_MONTHS) {
    month++;

    // Apply monthly interest and minimum payments to all debts
    // eslint-disable-next-line no-loop-func
    remaining = remaining.map(d => {
      if (d.paidOff) return d;
      const monthlyRate = (d.interestRate || 0) / 100 / 12;
      const interest    = d.balance * monthlyRate;
      totalInterest    += interest;
      const newBal      = d.balance + interest - Math.min(d.minimumPayment || 0, d.balance + interest);
      const paidOff     = newBal <= 0.01;
      return { ...d, balance: paidOff ? 0 : newBal, paidOff, monthPaidOff: paidOff ? month : null };
    });

    // Apply extra payment to first unpaid debt (in strategy order)
    let extraLeft = extraPayment || 0;
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].paidOff) continue;
      const pay = Math.min(extraLeft, remaining[i].balance);
      remaining[i] = { ...remaining[i], balance: remaining[i].balance - pay };
      if (remaining[i].balance <= 0.01) {
        remaining[i] = { ...remaining[i], balance: 0, paidOff: true, monthPaidOff: month };
        extraLeft -= pay;
        // Roll over remaining extra to next debt
      } else {
        break;
      }
    }
  }

  // Build payoff schedule (monthly totals for chart — first 60 months)
  const scheduleDebts = sorted.map(d => ({
    ...d, balance: d.balance || 0, paidOff: false,
  }));
  const schedule = [];
  let schedExtra    = extraPayment || 0;
  for (let m = 0; m < Math.min(60, month + 1); m++) {
    let rowTotal = 0;
    for (let i = 0; i < scheduleDebts.length; i++) {
      if (scheduleDebts[i].paidOff) continue;
      const rate = (scheduleDebts[i].interestRate || 0) / 100 / 12;
      const int  = scheduleDebts[i].balance * rate;
      const newB = scheduleDebts[i].balance + int - Math.min(scheduleDebts[i].minimumPayment || 0, scheduleDebts[i].balance + int);
      scheduleDebts[i].balance = Math.max(0, newB);
      rowTotal += scheduleDebts[i].balance;
    }
    // Extra payment
    let ex = schedExtra;
    for (let i = 0; i < scheduleDebts.length; i++) {
      if (scheduleDebts[i].paidOff || scheduleDebts[i].balance <= 0) { scheduleDebts[i].paidOff = true; continue; }
      const pay = Math.min(ex, scheduleDebts[i].balance);
      scheduleDebts[i].balance -= pay;
      ex -= pay;
      if (scheduleDebts[i].balance <= 0) scheduleDebts[i].paidOff = true;
      if (ex <= 0) break;
    }
    schedule.push({ month: m + 1, 'Remaining Balance': Math.round(Math.max(0, rowTotal)) });
  }

  // Per-debt payoff months
  const debtResults = sorted.map(d => {
    const r = remaining.find(r => r.id === d.id);
    return { ...d, monthPaidOff: r?.monthPaidOff };
  });

  return { months: month >= MAX_MONTHS ? null : month, totalInterest, schedule, debtResults };
}

function monthsToDate(months) {
  if (!months) return 'Over 50 years';
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function monthsToStr(months) {
  if (!months) return '50+ yrs';
  if (months < 12) return `${months}mo`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m > 0 ? `${y}y ${m}mo` : `${y}yr`;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DebtPlanner() {
  const { userProfile, updateSection } = useUserData();
  const debts = useMemo(() => userProfile?.debts || [], [userProfile?.debts]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showAdd,    setShowAdd]    = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [extraPmt,   setExtraPmt]   = useState(200);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    if (!form.name.trim() || !form.balance) return;
    const debt = {
      id:             crypto.randomUUID(),
      name:           form.name.trim(),
      type:           form.type,
      balance:        parseFloat(form.balance),
      interestRate:   parseFloat(form.interestRate) || 0,
      minimumPayment: parseFloat(form.minimumPayment) || 0,
    };
    updateSection('user_profile', { ...userProfile, debts: [...debts, debt] });
    setForm(EMPTY_FORM);
    setShowAdd(false);
  };

  const handleDelete = (id) => {
    updateSection('user_profile', { ...userProfile, debts: debts.filter(d => d.id !== id) });
  };

  // ── Calculations ──────────────────────────────────────────────────────────
  const totalBalance = debts.reduce((s, d) => s + (d.balance || 0), 0);
  const totalMinPmt  = debts.reduce((s, d) => s + (d.minimumPayment || 0), 0);
  const avgRate      = debts.length > 0
    ? debts.reduce((s, d) => s + (d.interestRate || 0), 0) / debts.length
    : 0;

  const snowball = useMemo(() => calcPayoff(debts, extraPmt, 'snowball'), [debts, extraPmt]);
  const avalanche = useMemo(() => calcPayoff(debts, extraPmt, 'avalanche'), [debts, extraPmt]);

  const savingsVsSnowball = snowball.totalInterest - avalanche.totalInterest;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs">
        <div className="font-medium text-slate-300 mb-1">Month {label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-400">{p.name}:</span>
            <span className="text-slate-200 font-medium">{fmt(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Debt Payoff Planner</h1>
          <p className="text-slate-400 text-sm mt-0.5">Compare snowball vs avalanche strategies</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={15} /> Add Debt
        </button>
      </div>

      {debts.length === 0 ? (
        <EmptyState
          icon="💳"
          title="No debts tracked"
          description="Add your credit cards, loans, and other debts to see a payoff plan and total interest calculations."
          actionLabel="Add First Debt"
          onAction={() => setShowAdd(true)}
        />
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Debt</div>
              <div className="text-xl font-bold text-rose-400">{fmt(totalBalance)}</div>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Min. Payments</div>
              <div className="text-xl font-bold text-slate-200">{fmt(totalMinPmt)}/mo</div>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Avg. Interest</div>
              <div className="text-xl font-bold text-amber-400">{avgRate.toFixed(1)}%</div>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Debts Count</div>
              <div className="text-xl font-bold text-slate-200">{debts.length}</div>
            </div>
          </div>

          {/* Extra payment input */}
          <div className="flex items-center gap-4 p-4 bg-slate-800 border border-slate-700 rounded-xl">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-200 block mb-0.5">Extra Monthly Payment</label>
              <p className="text-xs text-slate-500">Additional amount above minimum payments. Small increases have huge impact!</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">$</span>
              <input
                type="number"
                value={extraPmt}
                onChange={e => setExtraPmt(Math.max(0, Number(e.target.value)))}
                className="w-28 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 text-center font-semibold"
                min={0}
              />
              <span className="text-slate-400 text-sm">/mo</span>
            </div>
          </div>

          {/* Strategy comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Snowball */}
            <StrategyCard
              title="❄️ Snowball Method"
              subtitle="Smallest balance first"
              months={snowball.months}
              totalInterest={snowball.totalInterest}
              color="blue"
              desc="Pay minimums on all debts, then throw extra money at your smallest balance. Great for motivation — you see wins faster!"
              debtResults={snowball.debtResults}
            />
            {/* Avalanche */}
            <StrategyCard
              title="🏔️ Avalanche Method"
              subtitle="Highest interest rate first"
              months={avalanche.months}
              totalInterest={avalanche.totalInterest}
              color="purple"
              desc="Pay minimums on all debts, then attack the highest interest rate first. Mathematically optimal — saves the most interest."
              debtResults={avalanche.debtResults}
              savings={savingsVsSnowball > 50 ? savingsVsSnowball : null}
            />
          </div>

          {/* Interest saved comparison */}
          {savingsVsSnowball !== 0 && (
            <div className={`flex items-start gap-3 p-4 rounded-xl border ${
              savingsVsSnowball > 0
                ? 'bg-emerald-950/30 border-emerald-700/40'
                : 'bg-blue-950/30 border-blue-700/40'
            }`}>
              <CheckCircle size={18} className={savingsVsSnowball > 0 ? 'text-emerald-400' : 'text-blue-400'} />
              <div>
                <p className="text-sm font-semibold text-slate-200">
                  {savingsVsSnowball > 0
                    ? `Avalanche saves ${fmt(savingsVsSnowball)} more in interest than Snowball`
                    : `Snowball and Avalanche are about equal for your debts`
                  }
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  The best method is the one you'll actually stick to. Snowball wins if you need motivation; Avalanche wins mathematically.
                </p>
              </div>
            </div>
          )}

          {/* Paydown chart */}
          {snowball.schedule.length > 1 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="font-semibold text-slate-200 text-sm mb-1">Paydown Trajectory (Snowball)</h3>
              <p className="text-xs text-slate-500 mb-4">Total remaining balance each month with {fmt(totalMinPmt + extraPmt)}/mo payment</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={snowball.schedule} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false}
                    label={{ value: 'Month', position: 'insideBottom', fill: '#64748b', fontSize: 10, offset: -2 }}
                  />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false}
                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Remaining Balance" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Debt list */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700/60">
              <h3 className="font-semibold text-slate-200 text-sm">Your Debts</h3>
            </div>
            <div className="divide-y divide-slate-700/40">
              {debts.map(d => {
                const typeInfo = DEBT_TYPES.find(t => t.id === d.type) || DEBT_TYPES[6];
                const monthly = (d.balance * (d.interestRate / 100) / 12);
                return (
                  <div key={d.id} className="flex items-center gap-4 px-5 py-3.5 group hover:bg-slate-800/60">
                    <span className="text-xl flex-shrink-0">{typeInfo.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-200">{d.name}</div>
                      <div className="text-xs text-slate-500">{typeInfo.label} · {d.interestRate}% APR · {fmt(d.minimumPayment)}/mo min</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-rose-400">{fmt(d.balance)}</div>
                      <div className="text-xs text-slate-500">{fmtFull(monthly)}/mo interest</div>
                    </div>
                    <button
                      onClick={() => handleDelete(d.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-600 hover:text-rose-400 transition-all rounded-lg hover:bg-rose-950/30"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setShowAdd(false)}
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h2 className="text-lg font-bold text-slate-100">Add Debt</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-500 hover:text-slate-300 p-1 rounded-lg hover:bg-slate-800">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pb-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Debt Name</label>
                <input
                  type="text"
                  placeholder="e.g. Chase Sapphire, Student Loan"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Type</label>
                <div className="relative">
                  <select
                    value={form.type}
                    onChange={e => set('type', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500 appearance-none"
                  >
                    {DEBT_TYPES.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
                  </select>
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Balance ($)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={form.balance}
                    onChange={e => set('balance', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">APR (%)</label>
                  <input
                    type="number"
                    placeholder="0.0"
                    value={form.interestRate}
                    onChange={e => set('interestRate', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                    min={0}
                    step={0.1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Min. Pmt ($)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={form.minimumPayment}
                    onChange={e => set('minimumPayment', e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                    min={0}
                  />
                </div>
              </div>
              {form.balance && form.interestRate && (
                <div className="p-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-xs text-slate-400">
                  Monthly interest:{' '}
                  <span className="text-rose-400 font-semibold">
                    {fmtFull(parseFloat(form.balance) * (parseFloat(form.interestRate) / 100) / 12)}
                  </span>
                </div>
              )}
              <button
                onClick={handleAdd}
                disabled={!form.name.trim() || !form.balance}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold py-2.5 rounded-xl transition-all text-sm"
              >
                Add Debt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StrategyCard({ title, subtitle, months, totalInterest, color, desc, debtResults, savings }) {
  const colorMap = {
    blue:   { border: 'border-blue-700/40',   bg: 'from-blue-900/30',   badge: 'bg-blue-600',   text: 'text-blue-400' },
    purple: { border: 'border-purple-700/40', bg: 'from-purple-900/30', badge: 'bg-purple-600', text: 'text-purple-400' },
  };
  const c = colorMap[color];

  return (
    <div className={`bg-gradient-to-b ${c.bg} to-slate-800/50 border ${c.border} rounded-2xl p-5`}>
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-base font-bold text-slate-100">{title}</h3>
        {savings && (
          <span className="text-xs bg-emerald-900/60 text-emerald-300 border border-emerald-700/50 rounded-full px-2 py-0.5 font-medium">
            Saves {fmt(savings)}
          </span>
        )}
      </div>
      <p className={`text-xs font-medium ${c.text} mb-3`}>{subtitle}</p>
      <p className="text-xs text-slate-400 leading-relaxed mb-4">{desc}</p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-900/60 rounded-xl p-3 text-center">
          <div className={`text-2xl font-bold ${c.text}`}>{monthsToStr(months)}</div>
          <div className="text-xs text-slate-500 mt-0.5">Payoff time</div>
        </div>
        <div className="bg-slate-900/60 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-rose-400">{fmt(totalInterest)}</div>
          <div className="text-xs text-slate-500 mt-0.5">Total interest</div>
        </div>
      </div>

      <div className="text-xs text-slate-400 mb-2 font-medium">Payoff order:</div>
      <div className="space-y-1.5">
        {(debtResults || []).map((d, i) => (
          <div key={d.id} className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded-full ${c.badge} text-white text-xs flex items-center justify-center flex-shrink-0 font-bold`}>
              {i + 1}
            </span>
            <span className="text-xs text-slate-300 flex-1 truncate">{d.name}</span>
            <span className="text-xs text-slate-500">
              {d.monthPaidOff ? monthsToDate(d.monthPaidOff) : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
